import { LoyaltyConfig } from "custom-types";
import { useMemo } from "react";
import { LoyaltyEarningStatus } from "types/loyalty";

const WINDOWS = [
  { key: "day", label: "Today", configKey: "max_points_per_day" },
  { key: "week", label: "This week", configKey: "max_points_per_week" },
  { key: "month", label: "This month", configKey: "max_points_per_month" },
] as const;

export interface LoyaltyLimitRow {
  label: string;
  cap: number;
  /** null when the usage figures couldn't be loaded — the cap itself is still known. */
  earned: number | null;
  remaining: number | null;
}

/**
 * Builds the rows behind the checkout's "Limit" dialog, plus the headroom used to clamp the
 * earn projection.
 *
 * The caps come from the loyalty config, which the storefront always has, so a window's limit is
 * shown even if the usage call fails; `earned`/`remaining` are enrichment from the earning-status
 * endpoint. That split matters — an empty row set genuinely means "no window caps configured",
 * whereas missing usage must not be rendered as "no limit applies".
 */
export function useLoyaltyLimits(
  loyaltyConfig?: LoyaltyConfig | null,
  status?: LoyaltyEarningStatus,
) {
  return useMemo(() => {
    const earning = status?.enabled ? status.earning : undefined;

    const loyaltyLimits: LoyaltyLimitRow[] = WINDOWS.flatMap(
      ({ key, label, configKey }) => {
        const cap = loyaltyConfig?.[configKey] ?? 0;
        // 0 is the "unlimited" sentinel — nothing to show for that window.
        if (!cap || cap <= 0) return [];

        return [
          {
            label,
            cap,
            earned: earning?.earned?.[key] ?? null,
            remaining: earning?.remaining?.[key] ?? null,
          },
        ];
      },
    );

    return {
      earnAllowance: earning?.max_earnable_now ?? null,
      loyaltyLimits,
      /** True once the usage figures are in; the dialog stays honest until then. */
      hasUsage: loyaltyLimits.some((row) => row.remaining !== null),
    };
  }, [loyaltyConfig, status]);
}
